import { Button } from '@/components/ui/button'
import { useDashboardStats } from '@/client/modules/dashboard/hooks/useDashboardStats'
import { StatsCards } from '@/client/modules/dashboard/components/StatsCards'
import { PopularQueries } from '@/client/modules/dashboard/components/PopularQueries'
import { ZeroResults } from '@/client/modules/dashboard/components/ZeroResults'
import { BarChart3, RefreshCw, AlertCircle } from 'lucide-react'

/**
 * Insights page — search analytics view extracted from the home dashboard.
 * Shows popular searches, zero-result queries, and overview stats.
 *
 * Home is now the high-level "is the store healthy" overview;
 * Insights is the deeper "what are people searching for" view.
 */
export function InsightsPage() {
  const { data: stats, isLoading, error, refetch, isFetching } = useDashboardStats()

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <BarChart3 className="h-6 w-6" />
            Insights
          </h1>
          <p className="text-muted-foreground mt-1">
            What customers are searching for and where the gaps are.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-4 rounded-lg bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load analytics. Please try refreshing.</span>
        </div>
      )}

      <StatsCards stats={stats?.overview} isLoading={isLoading} />

      <div className="grid gap-6 lg:grid-cols-2">
        <PopularQueries queries={stats?.popularQueries} isLoading={isLoading} />
        <ZeroResults queries={stats?.zeroResultQueries} isLoading={isLoading} />
      </div>
    </div>
  )
}
